import { CheckCircle, Loader2, Mail, Sparkles } from "lucide-react";
import { useSnackbar } from "../hooks/use-toast";
import { useState } from "react";

// Simple className utility function
const cn = (...classes: (string | undefined | null | false)[]): string => {
  return classes.filter(Boolean).join(" ");
};

// Simple Input component
interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  className?: string;
}

const Input: React.FC<InputProps> = ({ className, ...props }) => {
  return (
    <input
      className={cn(
        "flex h-10 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      {...props}
    />
  );
};

// Simple Button component
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "destructive" | "outline" | "ghost" | "link";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
}

const Button: React.FC<ButtonProps> = ({
  variant = "default",
  size = "default",
  className,
  children,
  ...props
}) => {
  const baseStyles =
    "inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50";

  const variantStyles = {
    default: "bg-primary text-primary-foreground hover:bg-primary/90",
    destructive: "bg-vestn-error text-white hover:bg-vestn-error/90",
    outline:
      "border border-border bg-background hover:bg-muted hover:text-foreground",
    ghost: "hover:bg-muted hover:text-foreground",
    link: "text-primary underline-offset-4 hover:underline",
  };

  const sizeStyles = {
    default: "h-10 px-4 py-2 rounded-xl",
    sm: "h-9 px-3 rounded-xl",
    lg: "h-11 px-8 rounded-xl",
    icon: "h-10 w-10 rounded-xl",
  };

  return (
    <button
      className={cn(
        baseStyles,
        variantStyles[variant],
        sizeStyles[size],
        "rounded-xl", // Force rounded-xl for more visible border radius
        className
      )}
      style={{ borderRadius: "0.75rem" }} // Fallback inline style (rounded-xl = 0.75rem)
      {...props}
    >
      {children}
    </button>
  );
};

interface NewsletterProps {
  variant?: "footer" | "standalone";
  className?: string;
}

export const Newsletter = ({
  variant = "footer",
  className,
}: NewsletterProps) => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes("@")) {
      enqueueSnackbar("Please enter a valid email address.", {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
      return;
    }

    setStatus("loading");

    // Simulate API call - Replace with actual API call when ready
    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // TODO: Replace with actual newsletter subscription API call
      // Example:
      // await subscribeToNewsletter(email);

      setStatus("success");
      enqueueSnackbar("You've been added to our newsletter.", {
        variant: "success",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });

      setTimeout(() => {
        setEmail("");
        setStatus("idle");
      }, 3000);
    } catch (error) {
      setStatus("error");
      enqueueSnackbar("Failed to subscribe. Please try again.", {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
      setTimeout(() => {
        setStatus("idle");
      }, 2000);
    }
  };

  if (variant === "standalone") {
    return (
      <div
        className={cn(
          "p-8 rounded-2xl bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/20",
          className
        )}
      >
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-primary" />
          <h3 className="text-xl font-semibold text-foreground">
            Stay Updated
          </h3>
        </div>
        <p className="text-muted-foreground mb-6">
          Get the latest investment opportunities, market insights, and platform
          updates delivered to your inbox.
        </p>
        <form
          onSubmit={handleSubmit}
          className="flex flex-col sm:flex-row gap-3"
        >
          <Input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={status === "loading" || status === "success"}
            className="flex-1 bg-background"
          />
          <Button
            type="submit"
            disabled={status === "loading" || status === "success"}
            className="gap-2 !rounded-xl"
            style={{ borderRadius: "0.75rem" }}
          >
            {status === "loading" && (
              <Loader2 className="w-4 h-4 animate-spin" />
            )}
            {status === "success" && <CheckCircle className="w-4 h-4" />}
            {status === "idle" && <Mail className="w-4 h-4" />}
            {status === "success" ? "Subscribed!" : "Subscribe"}
          </Button>
        </form>
      </div>
    );
  }

  // Footer variant
  return (
    <div className={cn("", className)}>
      <h4 className="font-semibold text-foreground mb-4">Newsletter</h4>
      <p className="text-sm text-muted-foreground mb-4">
        Stay updated with the latest investment opportunities.
      </p>
      <form onSubmit={handleSubmit} className="space-y-3">
        <Input
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={status === "loading" || status === "success"}
          className="bg-background"
        />
        <Button
          type="submit"
          size="sm"
          disabled={status === "loading" || status === "success"}
          className="w-full gap-2 !rounded-xl"
          style={{ borderRadius: "0.75rem" }}
        >
          {status === "loading" && <Loader2 className="w-4 h-4 animate-spin" />}
          {status === "success" && <CheckCircle className="w-4 h-4" />}
          {status === "idle" && <Mail className="w-4 h-4" />}
          {status === "success" ? "Subscribed!" : "Subscribe"}
        </Button>
      </form>
    </div>
  );
};
